"use client"

import type React from "react"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Checkbox } from "@/components/ui/checkbox"
import { useToast } from "@/hooks/use-toast"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Search, Plus, Users } from "lucide-react"
import { apiFetch } from "@/lib/api/apiFetch"
import { getContactStatusColor } from "@/utils/common"
import { useHash } from "@/hooks/useHash"
import { useRouter } from "next/navigation"
import { Badge } from "./ui/badge"

interface Status {
  _id: string
  name: string
  value: string
  colorCode?: string
  type: string
  __v: number
  createdAt: string
  updatedAt: string
}
interface Contact {
  _id: string
  email: string
  name: string
  status: any
  source: string
  location?: string
}

interface RecipientList {
  _id: string
  name: string
  description: string
  remarks: string
  contactCount: number
  contacts: Contact[]
  createdAt: string
  updatedAt: string
}

interface CreateRecipientListDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedContacts: any[]
  onListCreated?: (list: RecipientList) => void
}

export function CreateRecipientListDialog({
  open,
  onOpenChange,
  selectedContacts,
  onListCreated,
}: CreateRecipientListDialogProps) {
  const router = useRouter();
  /**
     * this is to check hash routing path
     */
  const hash = useHash();
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    remarks: "",
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { toast } = useToast()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.name.trim()) {
      toast({
        title: "Validation Error",
        description: "List name is required",
        variant: "destructive",
      })
      return
    }

    if (selectedContacts.length === 0) {
      toast({
        title: "Validation Error",
        description: "Please select at least one contact",
        variant: "destructive",
      })
      return
    }

    setIsSubmitting(true)

    try {
      const newList: RecipientList = await apiFetch("/api/recipient-lists", {
        method: "POST",
        data: {
          name: formData.name.trim(),
          description: formData.description.trim(),
          remarks: formData.remarks.trim(),
          contactIds: selectedContacts.map((c) => c.id),
          createdBy: "system",
        },
      })

      toast({
        title: "Success",
        description: `Recipient list "${formData.name}" created with ${selectedContacts.length} contacts`,
      })

      if (onListCreated) {
        onListCreated(newList)
      }

      // Reset form
      setFormData({
        name: "",
        description: "",
        remarks: "",
      })

      onOpenChange(false)
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to create recipient list",
        variant: "destructive",
      })
    } finally {
      setIsSubmitting(false)
      if (hash === "#create-list") {
        router.push("/lists")
      }
    }
  }


  const getStatusDisplay = (status: string | { _id: string; name: string; value: string; colorCode?: string; type: string; __v: number; createdAt: string; updatedAt: string }) => {
    // Extract the status display name from either string or object
    return typeof status === 'string' ? status : status.name
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-max">
        <DialogHeader>
          <DialogTitle>Create Recipient List</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">List Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter list name"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of this list"
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="remarks">Remarks</Label>
            <Textarea
              id="remarks"
              value={formData.remarks}
              onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
              placeholder="Add notes or additional information"
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label>Selected Contacts ({selectedContacts.length})</Label>
            <div className="max-h-32 overflow-y-auto border rounded-md p-3">
              {selectedContacts.length > 0 ? (
                <div className="space-y-2">
                  {selectedContacts.slice(0, 5).map((contact) => (
                    <div key={contact.id} className="flex items-center gap-2">
                       <Users className="h-3 w-3 text-gray-500" />
                       <span className="text-sm font-medium">{contact.email}</span>
                       <Badge
                         style={getContactStatusColor(contact.status?.colorCode)}
                         className="status-badge-custom text-[10px] px-1.5 py-0 h-5 leading-none font-semibold transition-colors duration-150"
                       >
                         {contact.status?.name}
                       </Badge>
                    </div>
                  ))}
                  {selectedContacts.length > 5 && (
                    <div className="text-sm text-gray-500 text-center pt-2">
                      ... and {selectedContacts.length - 5} more contacts
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-gray-500 text-center">No contacts selected</div>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || selectedContacts.length === 0}>
              {isSubmitting ? "Creating..." : "Create List"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
